import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React, { FC, useContext, useEffect } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { getErrorTitle } from "../../../../constants/constants";
import { AdmissionsSection, FaqItem } from "../../../../models/manageWebSite";
import { normalizePlainText } from "../../../../utils/securityUtils";
import { appAccentPanelStyle, appDialogContentStyle, appDialogHeaderStyle, appDialogStyle, appFormSectionStyle, appMutedTextStyle, renderDialogFooter, renderSurfaceHeader } from "../../../../components/general/uiSurfaceStyles";

interface Props {
  data: AdmissionsSection | null;
  isOpen: boolean;
  toggleModal: (open: boolean) => void;
  flag: boolean;
  setFlag: (flag: boolean) => void;
  homeId: string | null;
}

type AdmissionsFormValues = {
  enabled: boolean;
  title: string;
  description: string;
  faqs: FaqItem[];
};

const EditModal: FC<Props> = ({ data, isOpen, toggleModal, flag, setFlag }) => {
  const toast = React.useRef<Toast>(null);
  const { backendAPIStore } = useContext(MobXProviderContext);
  const { control, handleSubmit, reset } = useForm<AdmissionsFormValues>({
    defaultValues: {
      enabled: data?.enabled || false,
      title: data?.title || "",
      description: data?.description || "",
      faqs: data?.faqs || [],
    },
  });
  const { fields, append, remove } = useFieldArray({ control, name: "faqs" });

  useEffect(() => {
    reset({
      enabled: data?.enabled || false,
      title: data?.title || "",
      description: data?.description || "",
      faqs: data?.faqs || [],
    });
  }, [data, isOpen, reset]);

  const onSubmit = async (formData: AdmissionsFormValues) => {
    try {
      await backendAPIStore.init();
      await backendAPIStore.updateTemplateSection({
        ...data,
        enabled: !!formData.enabled,
        title: normalizePlainText(formData.title, { maxLength: 140 }),
        description: normalizePlainText(formData.description, { multiline: true, maxLength: 1200 }),
        availability: "",
        pricingSnapshot: "",
        admissionsProcess: [],
        faqs: formData.faqs
          .map((item) => ({
            question: normalizePlainText(item.question, { maxLength: 220 }),
            answer: normalizePlainText(item.answer, { multiline: true, maxLength: 1200 }),
          }))
          .filter((item) => item.question && item.answer),
        modifiedAt: new Date().toISOString(),
      });
      toggleModal(false);
      setFlag(!flag);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: "Unable to save the FAQ page.", life: 3000 });
    }
  };

  return (
    <Dialog visible={isOpen} modal header={renderSurfaceHeader("FAQ Page", "Manage the most common questions families ask before choosing your home.", "pi pi-question-circle")} footer={renderDialogFooter({ errorMessage: null, loading: false, onCancel: () => toggleModal(false), onSubmit: handleSubmit(onSubmit), submitLabel: "Save FAQs" })} style={appDialogStyle} headerStyle={appDialogHeaderStyle} contentStyle={appDialogContentStyle} onHide={() => toggleModal(false)}>
      <Toast ref={toast} />
      <div style={appAccentPanelStyle} className="mb-3">
        <div style={{ fontWeight: 700, color: "#b45309" }}>FAQ page</div>
        <div className="mt-2" style={appMutedTextStyle}>Add each question and answer separately so they can be highlighted clearly on the website.</div>
      </div>
      <form>
        <div style={appFormSectionStyle}>
          <div className="flex align-items-center gap-2">
            <Controller name="enabled" control={control} render={({ field }) => <Checkbox inputId="admissionsEnabled" checked={!!field.value} onChange={(e) => field.onChange(!!e.checked)} />} />
            <label htmlFor="admissionsEnabled" style={{ fontWeight: 600 }}>Add FAQ page into the website</label>
          </div>
        </div>
        {[
          { name: "title", label: "Page Title", rows: 3 },
          { name: "description", label: "Description", rows: 4 },
        ].map(({ name, label, rows }) => (
          <div style={{ ...appFormSectionStyle, marginTop: "1rem" }} key={name}>
            <label htmlFor={name}><strong>{label}</strong></label>
            <Controller name={name as any} control={control} render={({ field }) => <InputTextarea id={name} {...field} rows={rows} style={{ width: "100%" }} />} />
          </div>
        ))}
        <div style={{ ...appFormSectionStyle, marginTop: "1rem" }}>
          <div className="flex justify-content-between align-items-center gap-3 mb-3">
            <label><strong>FAQs</strong></label>
            <Button type="button" label="Add FAQ" icon="pi pi-plus" severity="warning" outlined onClick={() => append({ question: "", answer: "" })} />
          </div>
          <div className="flex flex-column gap-3">
            {fields.map((field, index) => (
              <div key={field.id} style={{ background: "#fffaf3", border: "1px solid #f0dcc2", borderRadius: "18px", padding: "1rem" }}>
                <div className="flex justify-content-between align-items-center gap-3 mb-3">
                  <div style={{ fontWeight: 700, color: "#92400e" }}>FAQ {index + 1}</div>
                  <Button type="button" icon="pi pi-trash" severity="danger" outlined rounded onClick={() => remove(index)} aria-label={`Remove FAQ ${index + 1}`} />
                </div>
                <div className="flex flex-column gap-3">
                  <div>
                    <label htmlFor={`faq-question-${index}`}><strong>Question</strong></label>
                    <Controller
                      name={`faqs.${index}.question`}
                      control={control}
                      render={({ field }) => <InputText id={`faq-question-${index}`} {...field} style={{ width: "100%" }} />}
                    />
                  </div>
                  <div>
                    <label htmlFor={`faq-answer-${index}`}><strong>Answer</strong></label>
                    <Controller
                      name={`faqs.${index}.answer`}
                      control={control}
                      render={({ field }) => <InputTextarea id={`faq-answer-${index}`} {...field} rows={4} style={{ width: "100%" }} />}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </form>
    </Dialog>
  );
};

export default EditModal;
